import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet/dist/images/marker-shadow.png';
import { getDisplayDateTimeFormat } from '../util/generalUtills';
import { getTileLayerConfig, getDefaultMapView } from '../util/mapProvider';
import { 
  FaTruck, 
  FaCar, 
  FaBatteryThreeQuarters, 
  FaBatteryFull, 
  FaBatteryHalf, 
  FaBatteryQuarter, 
  FaBatteryEmpty,
  FaPlug,
  FaTachometerAlt,
  FaMapMarkerAlt,
  FaClock,
  FaBuilding,
  FaCheckCircle,
  FaPauseCircle,
  FaExclamationCircle
} from 'react-icons/fa';

// Get vehicle icon SVG based on vehicle type (Font Awesome icons)
const getVehicleIconSVG = (vehicleType, color) => {
  // Top-down view Car SVG (rooftop view)
  const carSVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" style="width: 32px; height: 32px;">
    <!-- Rear wheels -->
    <circle cx="50" cy="150" r="15" fill="${color}" opacity="0.8" stroke="#000" stroke-width="2"/>
    <circle cx="50" cy="150" r="10" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="50" cy="150" r="5" fill="#000"/>
    
    <circle cx="150" cy="150" r="15" fill="${color}" opacity="0.8" stroke="#000" stroke-width="2"/>
    <circle cx="150" cy="150" r="10" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="150" cy="150" r="5" fill="#000"/>
    
    <!-- Front wheels -->
    <circle cx="50" cy="50" r="12" fill="${color}" opacity="0.8" stroke="#000" stroke-width="2"/>
    <circle cx="50" cy="50" r="8" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="50" cy="50" r="4" fill="#000"/>
    
    <circle cx="150" cy="50" r="12" fill="${color}" opacity="0.8" stroke="#000" stroke-width="2"/>
    <circle cx="150" cy="50" r="8" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="150" cy="50" r="4" fill="#000"/>
    
    <!-- Car body (rounded rectangle) -->
    <rect x="40" y="60" width="120" height="80" rx="8" fill="${color}" opacity="0.9" stroke="#000" stroke-width="2"/>
    
    <!-- Windshield -->
    <rect x="60" y="70" width="80" height="25" rx="3" fill="none" stroke="#000" stroke-width="1.5" opacity="0.4"/>
    
    <!-- Side windows -->
    <rect x="70" y="100" width="30" height="20" rx="2" fill="none" stroke="#000" stroke-width="1" opacity="0.3"/>
    <rect x="100" y="100" width="30" height="20" rx="2" fill="none" stroke="#000" stroke-width="1" opacity="0.3"/>
  </svg>`;
  
  // Top-down view Tractor SVG (detailed rooftop view)
  const tractorSVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" style="width: 32px; height: 32px;">
    <!-- Large Rear Wheels (Tractor tires) -->
    <circle cx="55" cy="155" r="22" fill="${color}" opacity="0.85" stroke="#000" stroke-width="2.5"/>
    <circle cx="55" cy="155" r="16" fill="none" stroke="#000" stroke-width="2"/>
    <circle cx="55" cy="155" r="10" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="55" cy="155" r="5" fill="#000"/>
    
    <circle cx="145" cy="155" r="22" fill="${color}" opacity="0.85" stroke="#000" stroke-width="2.5"/>
    <circle cx="145" cy="155" r="16" fill="none" stroke="#000" stroke-width="2"/>
    <circle cx="145" cy="155" r="10" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="145" cy="155" r="5" fill="#000"/>
    
    <!-- Front Wheels (smaller, steering wheels) -->
    <circle cx="45" cy="50" r="14" fill="${color}" opacity="0.85" stroke="#000" stroke-width="2"/>
    <circle cx="45" cy="50" r="10" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="45" cy="50" r="5" fill="#000"/>
    
    <circle cx="155" cy="50" r="14" fill="${color}" opacity="0.85" stroke="#000" stroke-width="2"/>
    <circle cx="155" cy="50" r="10" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="155" cy="50" r="5" fill="#000"/>
    
    <!-- Engine Hood (front) -->
    <ellipse cx="100" cy="45" rx="35" ry="18" fill="${color}" opacity="0.9" stroke="#000" stroke-width="2"/>
    <ellipse cx="100" cy="45" rx="28" ry="14" fill="none" stroke="#000" stroke-width="1.5" opacity="0.5"/>
    
    <!-- Main Cabin (center) -->
    <rect x="70" y="75" width="60" height="45" rx="6" fill="${color}" opacity="0.95" stroke="#000" stroke-width="2.5"/>
    
    <!-- Cabin Windows -->
    <rect x="75" y="80" width="50" height="20" rx="3" fill="none" stroke="#000" stroke-width="1.5" opacity="0.4"/>
    <rect x="78" y="83" width="44" height="14" rx="2" fill="rgba(135, 206, 250, 0.3)"/>
    
    <!-- Cabin Door -->
    <line x1="100" y1="100" x2="100" y2="120" stroke="#000" stroke-width="1.5" opacity="0.6"/>
    
    <!-- Rear Section (transmission/hitch area) -->
    <rect x="85" y="120" width="30" height="25" rx="4" fill="${color}" opacity="0.8" stroke="#000" stroke-width="2"/>
    
    <!-- Rear Hitch Point -->
    <circle cx="100" cy="148" r="4" fill="#000" opacity="0.7"/>
    <rect x="98" y="148" width="4" height="8" fill="#000" opacity="0.6"/>
    
    <!-- Front Grille/Attachment Point -->
    <rect x="90" y="35" width="20" height="12" rx="2" fill="none" stroke="#000" stroke-width="1.5" opacity="0.6"/>
    
    <!-- Exhaust Stack (optional detail) -->
    <rect x="75" y="70" width="4" height="8" rx="1" fill="#000" opacity="0.5"/>
  </svg>`;
  
  if (vehicleType?.toLowerCase() === 'tractor') {
    return tractorSVG;
  }
  return carSVG;
};

// Define custom icons (rotatable using a DivIcon with Font Awesome icons)
const createRotatedCarIcon = (heading, vehicleType, isCharging, speed) => {
  const headingValue = heading ? parseFloat(heading) : 0;
  const speedValue = parseFloat(speed) || 0;
  // Red for speed 0, Green for speed > 0
  const speedColor = speedValue > 0 ? '#28a745' : '#dc3545';
  const iconSVG = getVehicleIconSVG(vehicleType, speedColor);
  const chargingBadge = isCharging ? '<div style="position: absolute; top: -3px; right: -3px; background: #28a745; border-radius: 50%; width: 14px; height: 14px; border: 2px solid white; z-index: 10; display: flex; align-items: center; justify-content: center; font-size: 8px; color: white;">⚡</div>' : '';
  
  return L.divIcon({
    className: "custom-div-icon",
    html: `<div style="position: relative; transform: rotate(${headingValue}deg); transform-origin: center center; display: inline-flex; align-items: center; justify-content: center;">
             <div style="position: absolute; width: 44px; height: 44px; border-radius: 50%; background-color: ${speedColor}; opacity: 0.2; z-index: 0;"></div>
             <div style="position: absolute; width: 40px; height: 40px; border-radius: 50%; border: 3px solid ${speedColor}; z-index: 1;"></div>
             <div style="position: relative; z-index: 2; display: flex; align-items: center; justify-content: center; filter: drop-shadow(0 0 3px ${speedColor});">
               ${iconSVG}
             </div>
             ${chargingBadge}
           </div>`,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
    popupAnchor: [0, -20],
  });
};

// Get battery icon based on level
const getBatteryIcon = (level) => {
  const batteryLevel = parseFloat(level) || 0;
  if (batteryLevel >= 0.75) return <FaBatteryFull style={{ color: '#28a745' }} />;
  if (batteryLevel >= 0.5) return <FaBatteryThreeQuarters style={{ color: '#ffc107' }} />;
  if (batteryLevel >= 0.25) return <FaBatteryHalf style={{ color: '#fd7e14' }} />;
  if (batteryLevel > 0) return <FaBatteryQuarter style={{ color: '#dc3545' }} />;
  return <FaBatteryEmpty style={{ color: '#6c757d' }} />;
};

// Get activity status icon
const getActivityIcon = (activityType) => {
  if (activityType?.toLowerCase() === 'still') {
    return <FaPauseCircle style={{ color: '#6c757d' }} />;
  }
  return <FaCheckCircle style={{ color: '#28a745' }} />;
};

// Helper component to fit map bounds based on vehicle positions
const FitMapBounds = ({ vehicleData = [] }) => {
  const map = useMap();

  useEffect(() => {
    if (vehicleData && vehicleData.length > 0) {
      const bounds = L.latLngBounds(vehicleData.map(vehicle => [parseFloat(vehicle.lat), parseFloat(vehicle.lng)]));
      map.fitBounds(bounds); // Fit the map to show all vehicle markers
    }
  }, [vehicleData, map]);

  return null;
};

// Animated Marker Component - Smoothly moves marker when position changes
const AnimatedMarker = ({ vehicle, previousPosition }) => {
  const markerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    const marker = markerRef.current;
    if (!marker) return;

    const currentLat = parseFloat(vehicle.lat);
    const currentLng = parseFloat(vehicle.lng);

    // If there's a previous position, animate from old to new
    if (previousPosition && 
        (previousPosition.lat !== currentLat || previousPosition.lng !== currentLng)) {
      const startLat = parseFloat(previousPosition.lat);
      const startLng = parseFloat(previousPosition.lng);
      const duration = 1000; // 1 second animation
      const startTime = Date.now();
      startTimeRef.current = startTime;

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function for smooth animation (ease-in-out)
        const easeProgress = progress < 0.5 
          ? 2 * progress * progress 
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;

        const lat = startLat + (currentLat - startLat) * easeProgress;
        const lng = startLng + (currentLng - startLng) * easeProgress;

        marker.setLatLng([lat, lng]);

        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(animate);
        }
      };

      // Start animation
      animationFrameRef.current = requestAnimationFrame(animate);
    } else {
      // No previous position, just set directly
      marker.setLatLng([currentLat, currentLng]);
    }

    // Update icon if heading, speed, or charging status changed
    const newIcon = createRotatedCarIcon(
      vehicle.heading, 
      vehicle.vehicleType, 
      vehicle.isCharging, 
      vehicle.speed
    );
    marker.setIcon(newIcon);

    // Cleanup animation on unmount or when vehicle changes
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [vehicle, previousPosition]);

  return (
    <Marker
      ref={markerRef}
      position={[parseFloat(vehicle.lat), parseFloat(vehicle.lng)]}
      icon={createRotatedCarIcon(vehicle.heading, vehicle.vehicleType, vehicle.isCharging, vehicle.speed)}
    >
      <Popup maxWidth={300} className="vehicle-popup">
        <div style={{ minWidth: '250px', fontFamily: 'Arial, sans-serif' }}>
          {/* Header */}
          <div style={{ 
            borderBottom: '2px solid #4fbaf6', 
            paddingBottom: '8px', 
            marginBottom: '12px' 
          }}>
            <h5 style={{ 
              margin: 0, 
              color: '#333', 
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              {vehicle.vehicleType?.toLowerCase() === 'tractor' ? (
                <FaTruck style={{ color: '#4fbaf6' }} />
              ) : (
                <FaCar style={{ color: '#4fbaf6' }} />
              )}
              <strong>{vehicle?.vehicleNumber || 'N/A'}</strong>
            </h5>
          </div>

          {/* Vehicle Info Grid */}
          <div style={{ display: 'grid', gap: '8px', fontSize: '13px' }}>
            {/* Vehicle Type */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaTruck style={{ color: '#6c757d', fontSize: '14px' }} />
              <span style={{ fontWeight: '500' }}>Type:</span>
              <span>{vehicle?.vehicleType || 'N/A'}</span>
            </div>

            {/* Activity Status */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {getActivityIcon(vehicle?.activityType)}
              <span style={{ fontWeight: '500' }}>Status:</span>
              <span style={{ 
                textTransform: 'capitalize',
                color: vehicle?.activityType?.toLowerCase() === 'still' ? '#6c757d' : '#28a745'
              }}>
                {vehicle?.activityType || 'N/A'}
              </span>
            </div>

            {/* Speed */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaTachometerAlt style={{ color: '#4fbaf6', fontSize: '14px' }} />
              <span style={{ fontWeight: '500' }}>Speed:</span>
              <span>
                {vehicle?.speed != null && vehicle?.speed !== '' 
                  ? `${parseFloat(vehicle.speed).toFixed(2)} km/h`
                  : '0.00 km/h'
                }
              </span>
            </div>

            {/* Battery Level */}
            {vehicle?.level !== null && vehicle?.level !== undefined && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {getBatteryIcon(vehicle.level)}
                <span style={{ fontWeight: '500' }}>Battery:</span>
                <span>{Math.round(parseFloat(vehicle.level) * 100)}%</span>
                {vehicle?.isCharging && (
                  <span style={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    gap: '4px',
                    color: '#28a745',
                    fontSize: '12px',
                    marginLeft: '4px'
                  }}>
                    <FaPlug style={{ fontSize: '10px' }} />
                    Charging
                  </span>
                )}
              </div>
            )}

            {/* Accuracy */}
            {vehicle?.accuracy && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaMapMarkerAlt style={{ color: '#fd7e14', fontSize: '14px' }} />
                <span style={{ fontWeight: '500' }}>Accuracy:</span>
                <span>{parseFloat(vehicle.accuracy).toFixed(2)} m</span>
              </div>
            )}

            {/* Last Location Time */}
            {vehicle?.lastLocation && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaClock style={{ color: '#6c757d', fontSize: '14px' }} />
                <span style={{ fontWeight: '500' }}>Last Update:</span>
                <span style={{ fontSize: '12px' }}>
                  {getDisplayDateTimeFormat(vehicle.lastLocation)}
                </span>
              </div>
            )}

            {/* NOB Status */}
            {vehicle?.nobStatus && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaExclamationCircle style={{ 
                  color: vehicle.nobStatus === 'Idle' ? '#ffc107' : '#28a745',
                  fontSize: '14px' 
                }} />
                <span style={{ fontWeight: '500' }}>NOB:</span>
                <span style={{ 
                  textTransform: 'capitalize',
                  color: vehicle.nobStatus === 'Idle' ? '#ffc107' : '#28a745'
                }}>
                  {vehicle.nobStatus}
                </span>
              </div>
            )}

            {/* JEN Info */}
            {vehicle?.jen?.name && (
              <div style={{ 
                marginTop: '8px', 
                paddingTop: '8px', 
                borderTop: '1px solid #e0e0e0',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px'
              }}>
                <FaBuilding style={{ color: '#6c757d', fontSize: '14px', marginTop: '2px' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '500', marginBottom: '4px' }}>JEN:</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {vehicle.jen.name}
                  </div>
                  {vehicle.jen.city && (
                    <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                      {vehicle.jen.city.cityName}, {vehicle.jen.city.state?.stateName}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Popup>
    </Marker>
  );
};

const LeafletMapAllVehicle = ({ mapHeight, vehicleData = [] }) => {
  const previousPositionsRef = useRef({});
  const previousVehicleDataRef = useRef([]);
  const generalSettingLists = useSelector((state) => state.generalSettingReducer.generalSettingLists);
  const mapProvider = generalSettingLists?.mapProvider;
  const tileLayerConfig = getTileLayerConfig(mapProvider);
  const defaultMapView = useMemo(() => getDefaultMapView(generalSettingLists), [generalSettingLists]);

  // Get previous position for a vehicle before updating the ref
  const getPreviousPosition = (vehicle) => {
    // Use vehicle.id since it's unique
    return previousPositionsRef.current[vehicle.id];
  };

  // Update previous positions after render (for next update cycle)
  useEffect(() => {
    if (vehicleData && vehicleData.length > 0) {
      // Use setTimeout to update previous positions after current render cycle
      const timeoutId = setTimeout(() => {
        const newPositions = {};
        vehicleData.forEach((vehicle) => {
          // Use vehicle.id since it's unique
          const currentLat = parseFloat(vehicle.lat);
          const currentLng = parseFloat(vehicle.lng);
          
          // Store current position as previous for next update
          newPositions[vehicle.id] = {
            lat: currentLat,
            lng: currentLng
          };
        });
        previousPositionsRef.current = newPositions;
        previousVehicleDataRef.current = vehicleData;
      }, 1100); // Update after animation completes (1000ms + buffer)

      return () => clearTimeout(timeoutId);
    }
  }, [vehicleData]);

  return (
    <div style={{ height: mapHeight }}>
      <MapContainer center={defaultMapView.center} zoom={defaultMapView.zoom} style={{ height: '100%' }}>
        <TileLayer
          key={mapProvider}
          url={tileLayerConfig.url}
          subdomains={tileLayerConfig.subdomains}
          attribution={tileLayerConfig.attribution}
          maxZoom={tileLayerConfig.maxZoom}
        />
        {/* Fit the map to the bounds of all vehicle markers */}
        <FitMapBounds vehicleData={vehicleData || []} />
        {/* Loop through the vehicleData array to render each vehicle marker with animation */}
        {vehicleData && vehicleData.length > 0 && vehicleData.map((vehicle, index) => {
          // Use vehicle.id as key since it's unique. If id is missing, use index to ensure uniqueness
          // Note: deviceId is NOT unique (multiple vehicles can have same deviceId)
          const vehicleId = vehicle.id || `vehicle-${index}-${vehicle.vehicleNumber || vehicle.deviceId || 'unknown'}`;
          
          // Get previous position before it gets updated
          const previousPosition = getPreviousPosition(vehicle);
          
          return (
            <AnimatedMarker
              key={vehicleId}
              vehicle={vehicle}
              previousPosition={previousPosition}
            />
          );
        })}
      </MapContainer>
    </div>
  );
};

export default LeafletMapAllVehicle;
